import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import {
  XMarkIcon,
  TrashIcon,
  UserIcon,
  MapPinIcon,
  CalendarIcon,
  TagIcon,
  InformationCircleIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { BourbonListing } from '../services/bourbonApi'
import { isAdmin } from '../utils/roles'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import toast from 'react-hot-toast'

interface ViewBourbonListingModalProps {
  isOpen: boolean
  onClose: () => void
  listing: BourbonListing | null
  onDelete: (id: string) => Promise<void>
  onEdit: (listing: BourbonListing) => void
}

export default function ViewBourbonListingModal({
  isOpen,
  onClose,
  listing,
  onDelete,
  onEdit
}: ViewBourbonListingModalProps) {
  const { user } = useAuth0()
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  if (!isOpen || !listing) return null

  const isOwner = user?.sub === listing.userId
  const userIsAdmin = isAdmin(user)
  const canDelete = isOwner || userIsAdmin

  // Debug logging
  console.log('ViewBourbonListingModal Debug:', {
    listing,
    isOwner,
    userMobile: listing.userMobile,
    userId: user?.sub,
    listingUserId: listing.userId
  })

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!canDelete) return

    try {
      setIsDeleting(true)
      await onDelete(listing.id)
      toast.success('Listing deleted successfully')
      setShowDeleteConfirm(false)
      onClose()
    } catch (error) {
      console.error('Error deleting listing:', error)
      toast.error('Failed to delete listing')
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const generateListingUrl = () => {
    const baseUrl = window.location.origin
    return `${baseUrl}/bourbon?id=${listing.id}`
  }

  const handleShare = () => {
    const listingUrl = generateListingUrl()
    const shareText = `Check out this bourbon listing from Midwest Railbirds:

${listing.title}
Brand: ${listing.brand}
${listing.type === 'have' ? 'Available for trade' : 'Looking to acquire'}
${listing.age ? `Age: ${listing.age} years` : ''}
${listing.proof ? `Proof: ${listing.proof}°` : ''}
${listing.location ? `Location: ${listing.location}` : ''}

Posted by: ${listing.userName}

${listing.description ? `Notes: ${listing.description}` : ''}`

    // For desktop fallback, include the URL in the text
    const shareTextWithUrl = `${shareText}

View full listing: ${listingUrl}`

    if (navigator.share) {
      // Use native share if available (mobile devices)
      // The URL will be handled separately by the native share
      navigator.share({
        title: `Bourbon Listing: ${listing.title}`,
        text: shareText,
        url: listingUrl
      }).catch(err => {
        console.log('Error sharing:', err)
        fallbackShare(shareTextWithUrl)
      })
    } else {
      // Fallback for desktop - include URL in text
      fallbackShare(shareTextWithUrl)
    }
  }

  const handleContactPoster = () => {
    const listingUrl = generateListingUrl()
    const contactText = `Hi! I saw your bourbon listing on Midwest Railbirds and I'm interested:

${listing.title} - ${listing.brand}
${listing.type === 'have' ? 'You have available' : 'You are looking for'}

${listing.description ? `Your notes: ${listing.description}` : ''}

View listing: ${listingUrl}

Let me know if you'd like to discuss!`

    // Create SMS link with phone number if available
    const phoneNumber = listing.userMobile || ''
    const smsUrl = phoneNumber 
      ? `sms:${phoneNumber}?body=${encodeURIComponent(contactText)}`
      : `sms:?body=${encodeURIComponent(contactText)}`
    
    if (navigator.share) {
      // Try native share first
      navigator.share({
        title: `Contact about: ${listing.title}`,
        text: contactText
      }).catch(() => {
        // Fallback to SMS
        window.open(smsUrl, '_blank')
      })
    } else {
      // Desktop fallback - show phone number if available
      if (phoneNumber) {
        const fullMessage = `${contactText}\n\nPhone: ${phoneNumber}`
        navigator.clipboard.writeText(fullMessage).then(() => {
          toast.success(`Contact message copied! Phone: ${phoneNumber}`)
        }).catch(() => {
          window.open(smsUrl, '_blank')
        })
      } else {
        navigator.clipboard.writeText(contactText).then(() => {
          toast.success('Contact message copied! You can paste it into any messaging app.')
        }).catch(() => {
          window.open(smsUrl, '_blank')
        })
      }
    }
  }

  const fallbackShare = (text: string) => {
    // Copy to clipboard
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Listing details copied to clipboard!')
    }).catch(() => {
      // If clipboard fails, try creating a text area
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast.success('Listing details copied to clipboard!')
      } catch (err) {
        toast.error('Unable to copy to clipboard')
      }
      document.body.removeChild(textArea)
    })
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl relative z-[61]"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  listing.type === 'have' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  {listing.type === 'have' ? 'Available' : 'Looking For'}
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  listing.status === 'available' 
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : listing.status === 'pending'
                    ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                    : 'bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400'
                }`}>
                  {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!isOwner && (
                  <button
                    onClick={handleContactPoster}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                    title="Contact poster"
                  >
                    <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleShare}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Share listing"
                >
                  <ShareIcon className="w-5 h-5" />
                </button>
                {(isOwner || userIsAdmin) && (
                  <button
                    onClick={() => onEdit(listing)}
                    className="p-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                    title="Edit listing"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete listing"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Images */}
              {listing.images && listing.images.length > 0 && (
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {listing.images.map((image, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                        <img
                          src={image}
                          alt={`${listing.title} - Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Details */}
              <div className={listing.images && listing.images.length > 0 ? 'lg:col-span-1' : 'lg:col-span-3'}>
                <div className="space-y-6">
                  {/* Title and Brand */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {listing.title}
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300">
                      {listing.brand}
                    </p>
                  </div>

                  {/* Specs */}
                  <div className="grid grid-cols-2 gap-4">
                    {listing.age && (
                      <div>
                        <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">Age</span>
                        <span className="text-lg text-gray-900 dark:text-white">
                          {typeof listing.age === 'number' ? `${listing.age} years` : listing.age}
                        </span>
                      </div>
                    )}
                    {listing.proof && (
                      <div>
                        <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">Proof</span>
                        <span className="text-lg text-gray-900 dark:text-white">{listing.proof}°</span>
                      </div>
                    )}
                    <div>
                      <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">Category</span>
                      <span className="text-lg text-gray-900 dark:text-white capitalize">{listing.category}</span>
                    </div>
                  </div>

                  {/* Description */}
                  {listing.description && (
                    <div>
                      <div className="flex items-center mb-2">
                        <InformationCircleIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900 dark:text-white">Description</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {listing.description}
                      </p>
                    </div>
                  )}

                  {/* Location */}
                  {listing.location && (
                    <div>
                      <div className="flex items-center mb-2">
                        <MapPinIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900 dark:text-white">Location</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">{listing.location}</p>
                    </div>
                  )}

                  {/* Trade Preferences */}
                  {listing.tradePreferences && (
                    <div>
                      <div className="flex items-center mb-2">
                        <TagIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900 dark:text-white">Trade Preferences</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {listing.tradePreferences}
                      </p>
                    </div>
                  )}

                  {/* User Info */}
                  <div>
                    <div className="flex items-center mb-2">
                      <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900 dark:text-white">Posted by</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">{listing.userName}</p>
                    {listing.userMobile && !isOwner && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        📱 {listing.userMobile}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <div className="flex items-center mb-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900 dark:text-white">Posted</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">{formatDate(listing.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {isOwner && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Your Listing
                    </span>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Listing"
        message={`Are you sure you want to delete "${listing.title}"? This action cannot be undone.`}
        confirmText="Delete Listing"
        isDeleting={isDeleting}
        countdownSeconds={3}
      />
    </AnimatePresence>
  )
}
